import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '@/components/layout/Layout';
import { PageLoading } from '@/components/common/PageLoading';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useHistoryStore } from '@/stores/history';
import { useUserInfoStore } from '@/stores/userInfoStore';
import { useChannelStore } from './stores/channelStore';
import { GlobalPlayerManager } from '@/utils/globalPlayerManager';
import { initTokenRefresh, clearTokenRefresh } from '@/utils/tokenRefreshManager';
import '@/utils/playerDebug'; // 导入调试工具，暴露到全局

// 首页保持同步加载（首屏关键路径）
import { Home } from '@/pages/Home';

// 其他页面使用懒加载
// 命名导出的组件
const FilterPage = lazy(() => import('@/pages/filterPage').then((module) => ({ default: module.FilterPage })));
const Play = lazy(() => import('@/pages/Play').then((module) => ({ default: module.Play })));
const Settings = lazy(() => import('@/pages/Settings').then((module) => ({ default: module.Settings })));
const History = lazy(() => import('@/pages/history/History').then((module) => ({ default: module.History })));
const About = lazy(() => import('@/pages/about').then((module) => ({ default: module.About })));
const SoftwareInfo = lazy(() =>
  import('@/pages/about/SoftwareInfo').then((module) => ({ default: module.SoftwareInfo }))
);
const EasterEgg = lazy(() => import('@/pages/about/EasterEgg').then((module) => ({ default: module.EasterEgg })));
const HelpFeedback = lazy(() => import('@/pages/HelpFeedback').then((module) => ({ default: module.HelpFeedback })));
const VipOpenPage = lazy(() =>
  import('@/pages/VipOpenPage/openPage/OpenPage').then((module) => ({ default: module.OpenPage }))
);
const VipOpenResPage = lazy(() =>
  import('@/pages/VipOpenPage/resultPage/ResultPage').then((module) => ({ default: module.ResultPage }))
);
const Account = lazy(() => import('@/pages/account').then((module) => ({ default: module.Account })));
const Search = lazy(() => import('@/pages/Search').then((module) => ({ default: module.Search })));
const PlayPage = lazy(() => import('@/pages/playDetail/PlayPage').then((module) => ({ default: module.PlayPage })));
// 默认导出的组件
const ChannelPage = lazy(() => import('@/pages/channel').then((module) => ({ default: module.ChannelPage })));

/**
 * App 路由内容组件
 * 需要放在 BrowserRouter 内部才能使用 useNavigate
 */
const AppRoutes: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { reset: resetHistory, setVuid } = useHistoryStore();
  const { userInfo, init: initUserInfo, reset: resetUserInfo } = useUserInfoStore();
  const updateChannelList = useChannelStore((state) => state.updateChannelList);

  // 票据失效弹窗状态
  const [showTokenExpiredDialog, setShowTokenExpiredDialog] = useState(false);

  useEffect(() => {
    // setVuid 时 会触发 history获取
    setVuid(userInfo?.uid || '');
  }, [userInfo?.uid, setVuid]);

  // 应用卸载时清理播放器
  useEffect(() => {
    initUserInfo();
    updateChannelList();

    // 初始化票据刷新管理器（对标 tv-linux 的刷票逻辑）
    // 传入票据失效回调，在 401 时显示弹窗
    initTokenRefresh(() => {
      setShowTokenExpiredDialog(true);
    });

    return () => {
      resetUserInfo();
      resetHistory();
      GlobalPlayerManager.getInstance().destroy();

      // 清理票据刷新定时器
      clearTokenRefresh();
    };
  }, []);

  /**
   * 处理票据失效弹窗的确认按钮
   * 跳转到登录页面
   */
  const handleTokenExpiredConfirm = () => {
    setShowTokenExpiredDialog(false);
    navigate('/account');
  };

  /**
   * 处理票据失效弹窗的取消按钮
   * 关闭弹窗（用户可以选择稍后登录）
   */
  const handleTokenExpiredCancel = () => {
    setShowTokenExpiredDialog(false);
  };

  return (
    <>
      <Layout>
        <Suspense fallback={<PageLoading />}>
          <Routes>
            {/* 首页 */}
            <Route path="/" element={<Home />} />
            {/* 频道页 */}
            <Route path="/channel/:channelId?/:channelType?" element={<ChannelPage />} />
            {/* 账号登录 - 使用统一的 Account 入口 */}
            <Route path="/account/*" element={<Account />} />
            {/* 搜索相关页面 */}
            <Route path="/search" element={<Search />} />
            <Route path="/search/:sourcePage/:id" element={<Search />} />
            <Route path="/search/:sourcePage" element={<Search />} />
            {/* 筛选页 */}
            <Route path="/more/:firstcategory" element={<FilterPage />} />
            <Route path="/more/*" element={<FilterPage />} />
            {/* 播放页 */}
            <Route path="/play/:cid/:vid?" element={<Play />} />
            {/* 播放详情页 */}
            <Route path="/playDetail/:cid/:vid?" element={<PlayPage />} />
            {/* 功能页面 */}
            <Route path="/settings" element={<Settings />} />
            <Route path="/history" element={<History />} />
            <Route path="/about" element={<About />} />
            <Route path="/about/software-info" element={<SoftwareInfo />} />
            {/* 调试彩蛋页面 */}
            <Route path="/about/easter-egg" element={<EasterEgg />} />
            {/* 帮助反馈页面 */}
            <Route path="/help-feedback" element={<HelpFeedback />} />
            {/* 会员开通页面 */}
            <Route path="/vip/openRes" element={<VipOpenResPage />} />
            <Route path="/vip/open/:sourceId?" element={<VipOpenPage />} />
          </Routes>
        </Suspense>
      </Layout>

      {/* 票据失效弹窗 */}
      <ConfirmDialog
        visible={showTokenExpiredDialog}
        title={t('login.tokenExpired')}
        confirmText={t('login.relogin')}
        cancelText={t('login.cancel')}
        onConfirm={handleTokenExpiredConfirm}
        onCancel={handleTokenExpiredCancel}
      />
    </>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppRoutes />
    </BrowserRouter>
  );
};

export default App;
